// prefers-reduced-motion, read safely (no window during server rendering, no matchMedia in some test environments).
import { useSyncExternalStore } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';
const mql = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(QUERY) : undefined);

/** True when the person has asked for less motion. */
export function prefersReducedMotion(): boolean {
  try { return mql()?.matches ?? false; } catch { return false; }
}

function subscribe(cb: () => void): () => void {
  const m = mql();
  if (!m) return () => {};
  try { m.addEventListener('change', cb); return () => m.removeEventListener('change', cb); }
  catch { m.addListener?.(cb); return () => m.removeListener?.(cb); } // older Safari
}

/** Live value of prefers-reduced-motion; re-renders when the setting changes. */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, prefersReducedMotion, () => false);
}

/** True when the Web Animations API can run here (false during server rendering and in jsdom). */
export function canAnimate(): boolean {
  return typeof document !== 'undefined' && typeof document.createElement('div').animate === 'function';
}

/** The current time on the document timeline in ms (the clock WAAPI start times use). */
export function timelineNow(): number {
  const t = typeof document !== 'undefined' ? document.timeline?.currentTime : null;
  return typeof t === 'number' ? t : typeof performance !== 'undefined' ? performance.now() : Date.now();
}
